import React from 'react';
import { Search, Lightbulb, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  loadingType?: 'find' | 'analyze';
  onSearch?: () => void;
  onAnalyze?: () => void;
  answerSize?: number;
  onAnswerSizeChange?: (size: number) => void;
}

const sizeOptions = [
  { value: 1, label: 'Réponse courte' },
  { value: 2, label: 'Réponse moyenne' },
  { value: 3, label: 'Réponse longue' },
];

const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder = "Votre question, mot-clé...",
  isLoading = false,
  loadingType,
  onSearch,
  onAnalyze,
  answerSize = 1,
  onAnswerSizeChange,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onAnalyze) {
      onAnalyze();
    }
  };

  const currentSize = sizeOptions.find((opt) => opt.value === answerSize) || sizeOptions[0];

  return (
    <div className="relative w-full">
      {/* Main container - Mistral style */}
      <div 
        className={cn(
          "relative flex flex-col gap-3 px-4 py-4",
          "bg-card",
          "border border-border rounded-xl",
          "shadow-sm focus-within:shadow-md focus-within:border-primary/30",
          "transition-shadow duration-200"
        )}
      >
        {/* Input field */}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            "w-full bg-transparent border-0 outline-none",
            "text-base text-foreground",
            "placeholder:text-muted-foreground/60"
          )}
        />
        
        {/* Action buttons row - Mistral style */}
        <div className="flex items-center gap-1">
          {/* Search button */}
          <button
            type="button"
            onClick={onSearch}
            disabled={isLoading}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1.5",
              "text-sm text-primary",
              "rounded-lg transition-colors duration-150",
              "sm:hover:bg-secondary sm:hover:text-primary/80",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            <Search className="w-4 h-4" />
            <span>Recherche</span>
          </button>

          {/* Réflexion button with dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={isLoading}
                className={cn(
                  "inline-flex items-center gap-1.5 px-3 py-1.5",
                  "text-sm text-primary",
                  "rounded-lg transition-colors duration-150",
                  "sm:hover:bg-secondary sm:hover:text-primary/80",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                <Lightbulb className="w-4 h-4" />
                <span>Réflexion</span>
                <ChevronDown className="w-3 h-3 ml-0.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[180px]">
              {sizeOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => {
                    onAnswerSizeChange?.(option.value);
                    onAnalyze?.();
                  }}
                  className={cn(
                    "cursor-pointer",
                    answerSize === option.value && "bg-primary/10 text-primary font-medium"
                  )}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Progress bar - only shown when loading */}
      {isLoading && (
        <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary/20 rounded-full overflow-hidden">
          <div
            className={cn(
              "h-full animate-shimmer",
              loadingType === 'analyze' && 'animate-progress-analyze',
              loadingType === 'find' && 'animate-progress-find'
            )}
          />
        </div>
      )}
    </div>
  );
};

export default SearchInput;
